/**
 * Nextcloud paths in Files routes (#98 part 3, Hunch dec_4a43edd873):
 * #/files/nextcloud/<exposedId>/<p-prefixed base64url segments>. The proxy
 * builds the tokens (gitterdan-website NextcloudRouteToken); imsight decodes
 * them only to reject garbage routes early. Both sides test against the same
 * fixture (tests/fixtures/nextcloud-route-tokens.json).
 */

export const MAX_SEGMENTS = 7;
const CHUNK = 198;
const MAX_PATH_BYTES = 1024;

export const NEXTCLOUD_SUBROUTE =
  /^nextcloud(\/[a-f0-9]{32}(\/p[A-Za-z0-9_-]{1,198}){0,7})?$/;

const SEGMENT = /^p([A-Za-z0-9_-]{1,198})$/;

/** The proxy's path rules (NextcloudPath::normalize); null = invalid. */
function normalize(path: string): string | null {
  if (new TextEncoder().encode(path).length > MAX_PATH_BYTES) return null;
  const nfc = path.normalize('NFC');
  if (/[\p{Cc}\\]/u.test(nfc)) return null;
  const trimmed = nfc.replace(/^\/+|\/+$/g, '');
  if (trimmed === '') return '';
  for (const part of trimmed.split('/')) {
    if (part === '' || part === '.' || part === '..') return null;
  }
  return trimmed;
}

function toBase64Url(text: string): string {
  let binary = '';
  for (const byte of new TextEncoder().encode(text))
    binary += String.fromCharCode(byte);
  return window
    .btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function fromBase64Url(b64: string): string | null {
  if (b64.length % 4 === 1) return null;
  try {
    const binary = window.atob(b64.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

export function encodeRouteToken(path: string): string[] {
  if (path === '') return [];
  const b64 = toBase64Url(path);
  const segments: string[] = [];
  for (let i = 0; i < b64.length; i += CHUNK)
    segments.push(`p${b64.slice(i, i + CHUNK)}`);
  return segments;
}

/** The relative path, or null for anything encodeRouteToken would not produce. */
export function decodeRouteToken(segments: string[]): string | null {
  if (segments.length === 0) return '';
  if (segments.length > MAX_SEGMENTS) return null;
  let b64 = '';
  for (const segment of segments) {
    const match = SEGMENT.exec(segment);
    if (!match) return null;
    b64 += match[1];
  }
  const raw = fromBase64Url(b64);
  if (raw === null || raw === '') return null;
  const path = normalize(raw);
  if (path === null || path !== raw) return null;
  const canonical = encodeRouteToken(path);
  return canonical.length === segments.length &&
    canonical.every((s, i) => s === segments[i])
    ? path
    : null;
}

/** 'nextcloud', 'nextcloud/<id>' or 'nextcloud/<id>/<token…>'; null when invalid. */
export function parseNextcloudSubroute(
  subroute: string
): { exposedId: string | null; path: string } | null {
  if (!NEXTCLOUD_SUBROUTE.test(subroute)) return null;
  const [, exposedId, ...token] = subroute.split('/');
  if (exposedId === undefined) return { exposedId: null, path: '' };
  const path = decodeRouteToken(token);
  return path === null ? null : { exposedId, path };
}
